/**
 * Pure helpers for the forced-update gate.
 *
 * The gate is driven by the Firestore document `config/app`:
 *
 *   { minVersion: '7.0.3', lockoutType: 'soft' | 'hard', lockoutMessage: '…',
 *     updateUrlIos?: '…', updateUrlAndroid?: '…' }
 *
 * `hooks/useVersionCheck.js` reads it and compares `minVersion` against the RUNNING BUNDLE's
 * `app.json` `expo.version`. These helpers live outside the hook so the comparison and the
 * soft-alert throttle can be unit-tested without React Native, Firestore or i18n.
 *
 * Two properties of the mechanism are worth stating here because they bound what a lockout
 * can achieve, and both are easy to get wrong when planning a ramp:
 *
 *  1. `expo.version` is the MARKETING version and it only changes on a native store build.
 *     Every OTA since 2026-06-27 reports `7.0.3`. So `minVersion` separates *native builds*,
 *     never OTA payloads — it cannot express "has the dual-write outbox" (which shipped as an
 *     OTA in #428). What it CAN express is "is on a runtime the OTA channel still serves".
 *  2. The gate only exists in bundles >= 6.9 (added 2025-12-31). Anything older never reads
 *     `config/app` at all and is unreachable by any value of `minVersion`.
 */

/** Storage key for the soft-alert throttle record. */
export const SOFT_ALERT_STORAGE_KEY = '@atlas/versionLockout/softAlert'

/** How long to stay quiet after showing the soft alert once, for one `minVersion`. */
export const SOFT_ALERT_INTERVAL_MS = 12 * 60 * 60 * 1000

/**
 * Compares two dotted version strings.
 * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2.
 *
 * Unparseable segments coerce to 0 (`NaN || 0`). That is deliberate and is the SAFE direction:
 * a typo in `minVersion` ('latest', 'v7.0.3') makes it compare as 0.x and the gate stays open,
 * rather than bricking every cashier over a bad config value. Kept byte-for-byte as it shipped
 * so moving it here is not a behaviour change; the tests pin the coercion so it stays that way.
 */
export function compareVersions(v1, v2) {
  const parts1 = String(v1).split('.').map(Number)
  const parts2 = String(v2).split('.').map(Number)

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0
    const p2 = parts2[i] || 0
    if (p1 < p2) return -1
    if (p1 > p2) return 1
  }
  return 0
}

/** True when the running bundle is strictly older than the configured floor. */
export function isVersionOlderThan(appVersion, minVersion) {
  return compareVersions(appVersion, minVersion) < 0
}

/** Serialise the throttle record. Kept as a helper so the shape has exactly one definition. */
export function encodeSoftAlertRecord({ minVersion, now }) {
  return JSON.stringify({ minVersion, at: now })
}

/** Parse the throttle record. Any corruption reads as "no record" -> the alert is shown. */
export function decodeSoftAlertRecord(raw) {
  if (typeof raw !== 'string' || raw.length === 0) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Should the dismissible soft-lockout alert be shown right now?
 *
 * `useVersionCheck` re-runs on every AppState -> 'active', i.e. every time the app is
 * foregrounded. Without a throttle a cashier who switches to the calculator and back gets a
 * modal alert every single time — on the NewSale screen, on top of a cart in progress. That
 * is what makes a soft step of the ramp unrunnable, so the throttle is part of the mechanism,
 * not a nicety.
 *
 * Rules:
 *  - no `minVersion` -> never (nothing to ask for)
 *  - no/corrupt record -> show (never silently swallow the first notice)
 *  - the record is for a DIFFERENT `minVersion` -> show (a ramp step is a new ask)
 *  - a record from the future (device clock moved backwards) -> show, rather than stay
 *    suppressed until the clock catches up
 *  - otherwise show once `intervalMs` has elapsed
 */
export function shouldShowSoftAlert({
  record,
  minVersion,
  now,
  intervalMs = SOFT_ALERT_INTERVAL_MS,
}) {
  if (!minVersion) return false
  if (!record || typeof record !== 'object') return true
  if (record.minVersion !== minVersion) return true
  const at = Number(record.at)
  if (!Number.isFinite(at)) return true
  if (at > now) return true
  return now - at >= intervalMs
}
